import get from 'lodash/get';
import extend from 'lodash/extend';
import pick from 'lodash/pick';
import * as AuthHelper from '@Services/Auth/AuthHelper';
import { Licenses, UsabillaLicenses } from '@Constants/CommonConstants';
import { FEATURES } from '@Constants/rx-ui/constants';
import CatalystConfig from '@Services/Catalyst/CatalystConfig';

const LicenseMap = {
  [Licenses.Reaxys]: UsabillaLicenses.Reaxys,
  [Licenses.ReaxysMedChem]: UsabillaLicenses.ReaxysMedChem,
  [Licenses.Both]: UsabillaLicenses.Both,
  none: UsabillaLicenses.None,
};

const defaultData = CatalystConfig.getDefaultVisitorData();
const extendedData = pick(defaultData, ['loginSuccess']);

const PRELOGIN_ACCESS_TYPES_OBJECT = {
  GUEST: 'Guest',
  GUEST_IP_ENFORCED: 'Guest (IP enforced)',
  GUEST_SHIBBOLETH_ENFORCED: 'Guest (Shibboleth enforced)',
};

export class AccountDataService {
  visitorData = null;

  PRELOGIN_ACCESS_TYPES = PRELOGIN_ACCESS_TYPES_OBJECT;

  /**
   * @ngdoc method
   * @name updateExtendedData
   * @methodOf Catalyst.AccountDataService
   * @description updates extended data, such as loginSuccess
   */
  updateExtendedData = (data) => {
    extend(extendedData, data);
  };

  getVisitorData() {
    this._initAccountData();
    return this.visitorData;
  }

  setVisitorAccessType = (accessType) => {
    if (!this.visitorData) {
      this._initAccountData();
    }

    if (this.visitorData.accessType && !this._isPreloginVisitorAccessType(this.visitorData.accessType)) {
      return;
    }

    if (this.visitorData.accessType && accessType === this.PRELOGIN_ACCESS_TYPES.GUEST) {
      return;
    }

    this.visitorData.accessType = accessType;
  };

  /**
   * @ngdoc function
   * @param {Object} visitorData current user data
   * @description initializes usabilla with required params (necessary for campaigns instantiation)
   */
  _setupUsabillaData = () => {
    if (window.usabilla_live) {
      window.usabilla_live('data', {
        custom: {
          userId: this.visitorData.userId,
          accountId: this.visitorData.accountId,
          accountName: this.visitorData.accountName,
          departmentId: this.visitorData.departmentId,
          license: this.visitorData.usabillaLicense,
        },
      });
    }
  };

  /**
   * @ngdoc function
   * @name _getUsabillaLicense
   * @param {String} [license]
   * @returns {String} with usabilla license corresponding to passed Reaxys license, otherwise - none
   * @description get usabilla license of current user
   */
  _getUsabillaLicense = (license) => {
    return LicenseMap[license || 'none'];
  };

  /**
   * @ngdoc function
   * @name initVisitorDetails
   * @description initializes page data with visitor's license for AA tracking
   */
  _initVisitorDetails = () => {
    const details = [
      `license=${this.visitorData.usabillaLicense}`,
      `retrosynthesis=${AuthHelper.isFeatureEnabled(FEATURES.pending_ai_rt) ? 'yes' : 'no'}`,
      `aisearch=${AuthHelper.isFeatureEnabled(FEATURES.texel_access_enabled) ? 'yes' : 'no'}`,
    ];

    this.visitorData.details = `rx:${details.join(':')}`;
  };

  _isPreloginVisitorAccessType = (accessType) => {
    return (
      accessType === this.PRELOGIN_ACCESS_TYPES.GUEST ||
      accessType === this.PRELOGIN_ACCESS_TYPES.GUEST_IP_ENFORCED ||
      accessType === this.PRELOGIN_ACCESS_TYPES.GUEST_SHIBBOLETH_ENFORCED
    );
  };

  _initAccountData = () => {
    const user = AuthHelper.getUser();
    const accountName = get(user, 'details.loginDetails.accountName');
    const accountId = get(user, 'details.loginDetails.accountId');
    if (accountName && accountId) {
      if (accountName.substring(0, 3) !== 'ae:') {
        user.details.loginDetails.accountName = `ae:${accountName}`;
      }
      if (accountId.substring(0, 3) !== 'ae:') {
        user.details.loginDetails.accountId = `ae:${accountId}`;
      }
    } else {
      this.visitorData = defaultData;
      this.visitorData.usabillaLicense = this._getUsabillaLicense();
    }

    const loginDetails = get(user, 'details.loginDetails', false);

    if (loginDetails) {
      this.visitorData = loginDetails;
      this.visitorData.usabillaLicense = this._getUsabillaLicense(user.details.license);
    } else {
      this.setVisitorAccessType(this.PRELOGIN_ACCESS_TYPES.GUEST);
    }

    this.visitorData.appSessionId = AuthHelper.getAccessToken();

    // extend visitor data with extended data
    extend(this.visitorData, extendedData);

    this._setupUsabillaData(this.visitorData);
    this._initVisitorDetails();
  };
}

const AccountData = new AccountDataService();
export default AccountData;
